import { getMarketActivity } from '@renderer/api/activityApi';
import { placeOrder as apiPlaceOrder, listOpenOrders as apiListOpenOrders, cancelOrder as apiCancelOrder, cancelAll as apiCancelAll } from '@renderer/api/ordersApi';
import { getPositions as apiGetPositions } from '@renderer/api/positionsApi';
import { onStatus as sessionOnStatus, onOrderbook as sessionOnOrderbook, refreshOrderbook as sessionRefreshOrderbook, subscribe as sessionSubscribe, log as sessionLog } from '@renderer/api/sessionApi';
import { pm } from './modules/api/pm';
import { createSessionController } from './modules/controllers/session';
import { loadOrders as loadOrdersFeature } from './modules/features/orders';
import { loadPositions as loadPositionsFeature } from './modules/features/positions';
import { handleError } from './modules/lib/errorHandler';
import { ErrorCategory, ErrorSeverity, deserializeAppError } from '@shared/error';
import { createOrderbookController } from './modules/orderbook/controller';
import { store } from './modules/state/store';
import { initControls } from './modules/ui/controls';
import { notifications } from './modules/ui/notifications';
import { el } from './modules/ui/selectors';
import { initShortcuts } from './modules/ui/shortcuts';

import type { Book, OrderSide } from './modules/types';

// Initialize notification system and expose globally for error handler
(window as any).notifications = notifications;

// Optional API key bootstrap for hosted backend security: ?pm_api_key=KEY
try {
  const url = new URL(window.location.href);
  const k = url.searchParams.get('pm_api_key') || url.searchParams.get('apiKey');
  if (k) {
    (window as any).__PM_API_KEY = k;
    try { localStorage.setItem('pm_api_key', k); } catch {}
  } else {
    const saved = localStorage.getItem('pm_api_key');
    if (saved) (window as any).__PM_API_KEY = saved;
  }
} catch {}

// Type definitions for global elements and functions
interface FlashElement extends HTMLElement {
  _t?: ReturnType<typeof setTimeout>;
}

// Early error/log hooks
try {
  window.onerror = function (msg, src, line, col, err) {
    try {
      console.error('[RendererError]', msg, src, line, col, err && err.stack);
      // Use notification system instead of alert
      handleError(
        err || new Error(String(msg)),
        ErrorCategory.SYSTEM,
        ErrorSeverity.HIGH,
        'An unexpected error occurred. Please refresh the page.',
        { src, line, col }
      );
    } catch (handlerErr) {
      // Fallback to alert if notification system fails
      try {
        alert('Renderer error: ' + msg);
      } catch (alertErr) {
        // Alert failed - nothing more we can do
      }
    }
  };
  console.log('[DBG] script start');
} catch (err) {
  const error = err as Error;
  console.warn('[App] Failed to set up error handlers:', error.message);
}

// Initialize orderbook controller early so its API can be passed to session
const orderbook = createOrderbookController({
  el,
  store,
  onRenderBookAfterLevels: () => {},
});
const books = store.books;
const ensurePanel = orderbook.ensurePanel;
const reorderBooks = (): void => {
  try {
    const a = getTeamAssetId('A');
    const b = getTeamAssetId('B');
    orderbook.reorderBooks(a, b);
  } catch (err) {
    const error = err as Error;
    console.warn('[App] Failed to reorder books:', error.message);
  }
};

// Initialize generic controls (theme, buttons, inputs)
const session = createSessionController({
  el,
  api: pm,
  store,
  ensurePanel,
  populateTeamMaps,
  syncTeamLabels,
  loadOrders,
  loadPositions,
  reorderBooks,
  append,
  loadFunds,
});

initControls({
  setCurrentSide: (side: OrderSide) => { store.currentSide = side; },
  updateNotional,
  onSubscribe: session.onSubscribeClick,
});

// market header/btn handled via session controller and controls
const slugInput = el.slugInput;
// Prefill with last used input if available (slug input may be missing)
try {
  const savedSlugInput = localStorage.getItem('pm_last_slug');
  if (savedSlugInput && slugInput) slugInput.value = savedSlugInput;
} catch (err) {
  const error = err as Error;
  console.warn('[App] Failed to load saved slug from localStorage:', error.message);
}

const assetSelect = el.assetSelect;
const teamBtnA = el.teamBtnA;
const teamBtnB = el.teamBtnB;
const priceInp = el.priceInp;
const sizeInp = el.sizeInp;
const quickSize = el.quickSize;
const orderResult = el.orderResult;
const flashEl = el.flashEl as FlashElement | null;
const maxSharesBtn = el.maxSharesBtn;
const add50Btn = el.add50Btn;

// Removed offset feature

// Allow mouse-wheel to adjust Quick size in increments of 10
if (quickSize) {
  quickSize.addEventListener(
    'wheel',
    (e: WheelEvent) => {
      try {
        e.preventDefault();
      } catch (err) {
        const error = err as Error;
        console.warn('[App] Failed to prevent wheel default:', error.message);
      }
      const cur = Number(quickSize.value) || 0;
      const step = 10;
      const dir = e.deltaY < 0 ? 1 : -1;
      let next = cur + dir * step;
      if (next < 0) next = 0;
      quickSize.value = String(next);
    },
    { passive: false }
  );
}

// Legacy helpers kept for now
function getTeamAssetId(team: 'A' | 'B'): string {
  try {
    if (team === 'A') {
      if (teamAMap && teamAMap.value) return teamAMap.value;
      const opt = assetSelect && assetSelect.options && assetSelect.options[0];
      return opt ? opt.value : '';
    } else {
      if (teamBMap && teamBMap.value) return teamBMap.value;
      const opt = assetSelect && assetSelect.options && assetSelect.options[1];
      return opt
        ? opt.value
        : assetSelect && assetSelect.options && assetSelect.options[0]
          ? assetSelect.options[0].value
          : '';
    }
  } catch {
    return '';
  }
}

/**
 * Updates team name labels based on current team mapping
 */
function syncTeamLabels(): void {
  try {
    const idA = getTeamAssetId('A');
    const idB = getTeamAssetId('B');
    const nameFor = (id: string): string => {
      if (!id) return '';
      const opts = assetSelect && assetSelect.options;
      if (opts) {
        for (let i = 0; i < opts.length; i++) {
          const opt = opts[i];
          if (opt && opt.value === id) {
            const text = opt.textContent || '';
            const parts = text.split(' (');
            return parts[0] || '';
          }
        }
      }
      const book = books.get(id);
      return (book && book.name) || id.slice(0, 8) + '…';
    };
    if (teamANameEl) teamANameEl.textContent = nameFor(idA);
    if (teamBNameEl) teamBNameEl.textContent = nameFor(idB);
  } catch (err) {
    const error = err as Error;
    console.warn('[App] Failed to sync team labels:', error.message);
  }
}

function populateTeamMaps(tokenIds: string[], outcomesById: Record<string, string>): void {
  if (!Array.isArray(tokenIds)) return;
  const buildOpts = (sel: HTMLSelectElement | null): void => {
    if (!sel) return;
    sel.innerHTML = '';
    for (const id of tokenIds) {
      const name = outcomesById && outcomesById[id] ? outcomesById[id] : id.slice(0, 8);
      const opt = document.createElement('option');
      opt.value = id;
      opt.textContent = `${name} (${id.slice(0, 8)}…)`;
      sel.appendChild(opt);
    }
  };
  buildOpts(teamAMap);
  buildOpts(teamBMap);
  // Restore persisted mapping for the current slug if available
  try {
    const slugKey = store.currentSlug || '';
    const raw = slugKey && localStorage.getItem('pm_team_map_' + slugKey);
    const saved = raw ? JSON.parse(raw) as { A?: string; B?: string } : null;
    const a = saved && saved.A && tokenIds.includes(saved.A) ? saved.A : (tokenIds[0] || '');
    const bCand = saved && saved.B && tokenIds.includes(saved.B) ? saved.B : (tokenIds[1] || tokenIds[0] || '');
    const b = a && bCand === a ? (tokenIds.find((x) => x !== a) || bCand) : bCand;
    if (teamAMap && a) teamAMap.value = a;
    if (teamBMap && b) teamBMap.value = b;
  } catch (err) {
    const error = err as Error;
    console.warn('[App] Failed to restore saved team mapping, using defaults:', error.message);
    const firstToken = tokenIds[0];
    const secondToken = tokenIds[1] || tokenIds[0];
    if (teamAMap && firstToken) teamAMap.value = firstToken;
    if (teamBMap && secondToken) teamBMap.value = secondToken;
  }
  syncTeamLabels();
  // Record initial team map version and timestamp upon population
  try {
    (store as any).teamMapVersion = ((store as any).teamMapVersion || 0) + 1;
    (store as any).lastTeamMapChangeTs = Date.now();
  } catch {}
  // Initialize team buttons labels if available
  try {
    const firstToken = tokenIds[0];
    const secondToken = tokenIds[1] || tokenIds[0];
    if (teamBtnA && firstToken) teamBtnA.textContent = outcomesById && outcomesById[firstToken] ? outcomesById[firstToken] : 'Team A';
    if (teamBtnB && secondToken) teamBtnB.textContent = outcomesById && outcomesById[secondToken] ? outcomesById[secondToken] : 'Team B';
  } catch (err) {
    const error = err as Error;
    console.warn('[App] Failed to initialize team button labels:', error.message);
  }

  // Re-assert team color classes after mapping changes
  try {
    const row = el.booksRow;
    if (row) {
      const children = Array.from(row.children);
      children.forEach((p) => { 
        try { 
          p.classList.remove('team-a'); 
          p.classList.remove('team-b'); 
        } catch (err) {
          const error = err as Error;
          console.warn('[App] Failed to remove team class from panel:', error.message);
        } 
      });
      const byId = new Map(children.map((p) => [(p as HTMLElement).dataset?.assetId || '', p]));
      const left = byId.get(teamAMap?.value || '');
      const right = byId.get(teamBMap?.value || '');
      if (left) left.classList.add('team-a');
      if (right) right.classList.add('team-b');
    }
  } catch (err) {
    const error = err as Error;
    console.warn('[App] Failed to re-assert team color classes:', error.message);
  }
}

const notionalEl = el.notionalEl;
const teamANameEl = el.teamANameEl;
const teamBNameEl = el.teamBNameEl;
const teamAMap = el.teamAMap;
const teamBMap = el.teamBMap;

/**
 * Finds an alternative token ID when team A/B mappings would collide
 */
function pickOtherTokenId(notId: string): string {
  try {
    const list = Array.isArray(store.currentAssetIds) ? store.currentAssetIds : [];
    for (const id of list) {
      if (id && id !== notId) return id;
    }
    const opts = el.assetSelect && el.assetSelect.options;
    if (opts) {
      for (let i = 0; i < opts.length; i++) {
        const id = opts[i]?.value;
        if (id && id !== notId) return id;
      }
    }
  } catch (err) {
    const error = err as Error;
    console.warn('[App] Failed to pick other token ID:', error.message);
  }
  return '';
}

let _syncingTeamMap = false;

function resolveTokenIdForPlaceOrder(): string {
  try {
    const aActive = !!(teamBtnA && teamBtnA.classList && teamBtnA.classList.contains('active'));
    const bActive = !!(teamBtnB && teamBtnB.classList && teamBtnB.classList.contains('active'));
    let id = '';
    // Prefer explicit active team buttons over dropdown selection
    if (aActive) id = getTeamAssetId('A');
    else if (bActive) id = getTeamAssetId('B');
    else {
      const cur = assetSelect && assetSelect.value;
      if (cur) id = cur;
      else id = getTeamAssetId('A');
    }
    if (!id && assetSelect && assetSelect.options && assetSelect.options[0]) id = assetSelect.options[0].value;
    if (assetSelect && id) assetSelect.value = id;
    return id || '';
  } catch { 
    return (assetSelect && assetSelect.value) || ''; 
  }
}

// Unified safe placeOrder wrapper with mapping/confirmation checks
async function safePlaceOrder(args: { tokenId: string; price: number; shares: number; side: OrderSide; orderType: 'GTC' | 'GTD' | 'FOK' }): Promise<ReturnType<typeof apiPlaceOrder>> {
  const { tokenId, price, shares, side, orderType } = args;
  // Guard: token must belong to current market
  try {
    const ids = Array.isArray(store.currentAssetIds) ? store.currentAssetIds : [];
    if (!ids.includes(tokenId)) {
      return { ok: false, error: 'Selected asset is not part of the current market' } as any;
    }
  } catch {}

  // Proceed to actual placement
  return apiPlaceOrder({ tokenId, price, shares, side, orderType });
}

function append(text: string | Record<string, unknown>): void {
  try {
    const line = typeof text === 'string' ? text : JSON.stringify(text);
    try {
      console.log('[DBG]', line);
    } catch {}
    // Forward only certain events to main to prevent log recursion
    try {
      const isForwardable = (obj: string | Record<string, unknown>): boolean => {
        if (!obj || typeof obj !== 'object') return true;
        if ('onStatus' in obj && obj.onStatus === 'debug') return false;
        if ('subscribeResult' in obj || 'subscribeError' in obj || ('click' in obj && obj.click === 'subscribe')) return true;
        if ('gotEvents' in obj) return false;
        return false;
      };
      if (isForwardable(text)) {
        sessionLog({
          ts: new Date().toISOString(),
          level: 'debug',
          module: 'renderer',
          event: 'append',
          line: text,
        });
      }
    } catch {}
  } catch (e) {
    try {
      console.log('[DBG-append-error]', e);
    } catch {}
  }
}

function flash(type: 'ok' | 'err' | '', message: string): void {
  // Use new notification system
  try {
    if (type === 'ok') {
      notifications.success(message, 2500);
    } else if (type === 'err') {
      notifications.error(message, 3000);
    } else {
      notifications.info(message, 2500);
    }
  } catch (notifErr) {
    // Fallback to old flash element
    if (!flashEl) return;
    try {
      flashEl.className = 'kv' + (type === 'ok' ? ' ok' : type === 'err' ? ' err' : '');
      flashEl.textContent = message;
      flashEl.style.opacity = '1';
      clearTimeout(flashEl._t);
      flashEl._t = setTimeout(() => {
        flashEl.style.opacity = '0';
      }, 2500);
    } catch {}
  }
}

const ordersEl = el.ordersEl;
const refreshOrdersBtn = el.refreshOrdersBtn;
const refreshBookBtn = el.refreshBookBtn;
const themeToggleBtn = document.getElementById('themeToggle');
const cancelAllBtn = el.cancelAllBtn;
const obStatus = el.obStatus;
const monoToggleBtn = document.getElementById('monoToggle') as HTMLButtonElement | null;
const refreshPositionsBtn = el.refreshPositionsBtn;
const positionsEl = el.positionsEl;
const activityEl = el.activityEl;
const refreshActivityBtn = el.refreshActivityBtn;

async function loadOrders(): Promise<void> {
  await loadOrdersFeature({
    currentAssetIds: store.currentAssetIds,
    listOpenOrders: apiListOpenOrders,
    ordersEl: ordersEl!,
    books,
    cancelOrder: apiCancelOrder,
    flash,
  });
}

async function loadPositions(): Promise<void> {
  await loadPositionsFeature({
    currentAssetIds: store.currentAssetIds,
    getPositions: apiGetPositions as any,
    positionsEl: positionsEl!,
    books,
    positionsByAssetId: store.positionsByAssetId,
  });
}

// Unified orderbook refresh used by button and keyboard shortcut
async function refreshOrderbook(): Promise<void> {
  try {
    const slug = (el.slugInput && el.slugInput.value) || '';
    const res = await sessionSubscribe(slug);
    if (res && res.ok) flash('ok', 'Orderbook refreshed');
    else {
      try {
        if (res && res.appError) notifications.showAppError(deserializeAppError(res.appError));
        else flash('err', 'Error: could not refresh orderbook');
      } catch {
        flash('err', 'Error: could not refresh orderbook');
      }
    }
  } catch (e) {
    const error = e as Error;
    flash('err', 'Orderbook refresh failed: ' + (error?.message || e));
  }
}

if (refreshOrdersBtn) refreshOrdersBtn.onclick = loadOrders;
if (refreshPositionsBtn) refreshPositionsBtn.onclick = loadPositions;
if (refreshBookBtn) refreshBookBtn.onclick = refreshOrderbook;
if (refreshActivityBtn) refreshActivityBtn.onclick = loadActivity;

// Theme toggle
if (themeToggleBtn) {
  try {
    const saved = localStorage.getItem('pm_theme_v2');
    if (saved) document.documentElement.setAttribute('data-theme', saved);
  } catch {}
  themeToggleBtn.onclick = () => {
    try {
      const root = document.documentElement;
      const cur = root.getAttribute('data-theme') || 'dark';
      const next = cur === 'ocean' ? 'dark' : 'ocean';
      root.setAttribute('data-theme', next);
      localStorage.setItem('pm_theme_v2', next);
      flash('', `Theme: ${next}`);
    } catch {}
  };
}

// Monochrome toggle
if (monoToggleBtn) {
  try {
    const saved = localStorage.getItem('pm_mono_book') === '1';
    if (saved) document.body.classList.add('mono-book');
  } catch {}
  monoToggleBtn.onclick = () => {
    try {
      document.body.classList.toggle('mono-book');
      const on = document.body.classList.contains('mono-book');
      localStorage.setItem('pm_mono_book', on ? '1' : '0');
      flash('', on ? 'Monochrome: ON' : 'Monochrome: OFF');
    } catch {}
  };
}

if (cancelAllBtn) {
  cancelAllBtn.onclick = async () => {
    cancelAllBtn.disabled = true;
    try {
      const res = await apiCancelAll();
      if (!res.ok) alert('Cancel all error: ' + res.error);
      else loadOrders();
    } finally {
      cancelAllBtn.disabled = false;
    }
  };
}

function updateNotional(): void {
  const price = Number(priceInp?.value);
  const shares = Number(sizeInp?.value);
  if (price > 0 && price <= 1 && shares > 0) {
    const usdc = price * shares;
    if (notionalEl) notionalEl.textContent = `USDC notional: ${usdc.toFixed(2)}`;
  } else {
    if (notionalEl) notionalEl.textContent = '';
  }
}

// On successful order placement, the exchange may take a moment to reflect
// the new open order. Refresh immediately and then once more shortly after.
let _ordersRefreshTimers: ReturnType<typeof setTimeout>[] = [];
let _ordersRefreshScheduled = false;
let _lastOrdersRefreshTs = 0;

function scheduleOrdersRefresh(): void {
  const now = Date.now();
  // If a refresh was scheduled recently, don't reschedule to avoid burst storms
  if (_ordersRefreshScheduled && now - _lastOrdersRefreshTs < 1000) return;
  _lastOrdersRefreshTs = now;
  _ordersRefreshScheduled = true;
  try {
    _ordersRefreshTimers.forEach((t) => clearTimeout(t));
  } catch {}
  _ordersRefreshTimers = [];
  // Immediate refresh
  loadOrders();
  // Staggered follow-ups
  _ordersRefreshTimers.push(setTimeout(loadOrders, 500));
  _ordersRefreshTimers.push(setTimeout(loadOrders, 2000));
  // Reset the scheduled flag after final follow-up
  _ordersRefreshTimers.push(setTimeout(() => { _ordersRefreshScheduled = false; }, 2200));
}

// Positions refresh helper similar to orders, to catch fills reflected with slight delay
let _positionsRefreshTimers: ReturnType<typeof setTimeout>[] = [];
let _positionsRefreshScheduled = false;
let _lastPositionsRefreshTs = 0;

function schedulePositionsRefresh(): void {
  const now = Date.now();
  if (_positionsRefreshScheduled && now - _lastPositionsRefreshTs < 1000) return;
  _lastPositionsRefreshTs = now;
  _positionsRefreshScheduled = true;
  try {
    _positionsRefreshTimers.forEach((t) => clearTimeout(t));
  } catch {}
  _positionsRefreshTimers = [];
  // Immediate refresh
  loadPositions();
  // Staggered follow-ups
  _positionsRefreshTimers.push(setTimeout(loadPositions, 1000));
  _positionsRefreshTimers.push(setTimeout(loadPositions, 3000));
  _positionsRefreshTimers.push(setTimeout(() => { _positionsRefreshScheduled = false; }, 3200));
}

// Activity refresh helper with coalescing to avoid heavy DOM/network bursts
let _activityRefreshTimers: ReturnType<typeof setTimeout>[] = [];
let _activityRefreshScheduled = false;
let _lastActivityRefreshTs = 0;

function scheduleActivityRefresh(): void {
  const now = Date.now();
  if (_activityRefreshScheduled && now - _lastActivityRefreshTs < 1500) return;
  _lastActivityRefreshTs = now;
  _activityRefreshScheduled = true;
  try { _activityRefreshTimers.forEach((t) => clearTimeout(t)); } catch {}
  _activityRefreshTimers = [];
  // Immediate refresh
  try { loadActivity(); } catch {}
  // Follow-up to catch late trades
  _activityRefreshTimers.push(setTimeout(loadActivity, 1500));
  _activityRefreshTimers.push(setTimeout(() => { _activityRefreshScheduled = false; }, 1700));
}

window.addEventListener('error', (e: ErrorEvent) => {
  const msg = 'Renderer error: ' + (e?.message || e);
  append(msg);
  try {
    alert(msg);
  } catch {}
});

session.wireStatus();

// Auto-load activity once subscribed to a market
sessionOnStatus((payload) => {
  try {
    if (payload && payload.type === 'subscribed') {
      loadActivity();
      // orderbook considered live upon subscription; will turn stale without messages
      store.obState = 'live';
      store.obLastHeartbeatTs = Date.now();
      if (obStatus) { obStatus.textContent = 'Orderbook: live'; obStatus.className = 'badge ok'; }
    } else if (payload && payload.type === 'closed') {
      store.obState = 'closed';
      if (obStatus) { obStatus.textContent = 'Orderbook: closed'; obStatus.className = 'badge'; }
    } else if (payload && payload.type === 'reconnecting') {
      store.obState = 'reconnecting';
      if (obStatus) { obStatus.textContent = 'Orderbook: reconnecting…'; obStatus.className = 'badge warn'; }
    } else if (payload && payload.type === 'error') {
      store.obState = 'error';
      if (obStatus) { obStatus.textContent = 'Orderbook: error'; obStatus.className = 'badge err'; }
    } else if (payload && payload.type === 'heartbeat') {
      // keep-alive from WS: connection healthy even if no book deltas
      if (store.obState !== 'closed' && store.obState !== 'error') {
        store.obLastHeartbeatTs = Date.now();
        // Do not force badge to live on heartbeat; timer will derive correct state
      }
    }
  } catch {}
});

sessionOnOrderbook((text: string) => {
  // Single-pass parse & apply; controller records trade-like hints
  try {
    const applied = orderbook.applyText(text);
    if (!applied) {
      const ef = store.obLastApplyErrorTs || 0;
      if (Date.now() - ef < 10000) {
        store.obState = 'error';
        if (obStatus) { obStatus.textContent = 'Orderbook: parse error — waiting for fresh data'; obStatus.className = 'badge err'; }
      }
    }
    // Decide side refresh based on controller hint
    try {
      const lastTradeLike = (store as any).obLastTradeLikeTs || 0;
      if (lastTradeLike && Date.now() - lastTradeLike < 2000) {
        scheduleOrdersRefresh();
        try { loadPositions(); } catch {}
        scheduleActivityRefresh();
      }
      updateOrderbookStatus();
    } catch {}
  } catch {
    append(text);
  }
});

// Wire BUY/SELL buttons to submit an order using current form values
async function placeFromForm(side: OrderSide): Promise<void> {
  const btn = side === 'BUY' ? el.sideBuyBtn : el.sideSellBtn;
  if (btn) btn.disabled = true;
  if (orderResult) orderResult.textContent = '';
  try {
    const tokenId = resolveTokenIdForPlaceOrder();
    const price = Number(priceInp?.value);
    const shares = Number(sizeInp?.value);
    const orderType = 'GTC';
    if (!tokenId) throw new Error('Select asset');
    if (!(price > 0 && price <= 1)) throw new Error('Invalid price');
    if (!(shares >= 5)) throw new Error('Minimum order size is 5 shares');
    const res = await safePlaceOrder({ tokenId, price, shares, side, orderType });
    if (res.ok) {
      const usd = (res.data && 'notional' in res.data ? res.data.notional : undefined) ?? price * shares;
      const prettyId =
        res.data && (res.data.orderID || res.data.orderId)
          ? String(res.data.orderID || res.data.orderId)
          : '';
      const shortId = prettyId ? `${prettyId.slice(0, 10)}…${prettyId.slice(-6)}` : '';
      if (orderResult) orderResult.textContent = `Order live: ${side} ${shares} @ ${price.toFixed(2)} (USDC ${usd.toFixed(2)}) ${shortId}`;
      scheduleOrdersRefresh();
      flash('ok', `Order placed: ${side} ${shares} @ ${price.toFixed(2)}`);
    } else {
      if (orderResult) orderResult.textContent = 'Submit error: ' + res.error;
      try {
        if (res.appError) {
          const appErr = deserializeAppError(res.appError);
          notifications.showAppError(appErr);
        } else {
          flash('err', 'Submit error: ' + res.error);
        }
      } catch {
        flash('err', 'Submit error: ' + res.error);
      }
    }
  } catch (e) {
    const error = e as Error;
    if (orderResult) orderResult.textContent = 'Submit error: ' + (error?.message || e);
    flash('err', 'Submit error: ' + (error?.message || e));
  } finally {
    if (btn) btn.disabled = false;
  }
}

// Team selection buttons behavior
function selectTeam(team: 'A' | 'B'): void {
  try {
    const id = team === 'A' ? getTeamAssetId('A') : getTeamAssetId('B');
    if (id && assetSelect) assetSelect.value = id;
    if (teamBtnA) teamBtnA.classList.toggle('active', team === 'A');
    if (teamBtnB) teamBtnB.classList.toggle('active', team === 'B');
    // Highlight team names consistently
    try {
      const aName = document.getElementById('teamAName');
      const bName = document.getElementById('teamBName');
      if (aName) aName.style.color = 'var(--teamA)';
      if (bName) bName.style.color = 'var(--teamB)';
    } catch {}
  } catch {}
}

if (teamBtnA) teamBtnA.onclick = () => selectTeam('A');
if (teamBtnB) teamBtnB.onclick = () => selectTeam('B');

// If user manually changes the asset dropdown, clear active team buttons to avoid ambiguity
if (assetSelect) {
  assetSelect.onchange = () => {
    try {
      if (teamBtnA) teamBtnA.classList.remove('active');
      if (teamBtnB) teamBtnB.classList.remove('active');
    } catch {}
  };
}

// Max shares button: set size to owned shares for selected asset
if (maxSharesBtn) {
  maxSharesBtn.onclick = () => {
    try {
      const id = assetSelect && assetSelect.value;
      const bal = id ? store.positionsByAssetId.get(id) : null;
      const owned = bal ? Math.floor(Number(bal) / 1e6) : 0;
      if (owned > 0) {
        if (sizeInp) sizeInp.value = String(owned);
        updateNotional();
        flash('ok', `Max shares set to ${owned}`);
      } else {
        flash('', 'No owned shares for selected team');
      }
    } catch {}
  };
}

// +50 shares helper
if (add50Btn) {
  add50Btn.onclick = () => {
    try {
      const cur = Number(sizeInp?.value) || 0;
      const next = cur + 50;
      if (sizeInp) sizeInp.value = String(next);
      updateNotional();
    } catch {}
  };
}

if (el.sideBuyBtn) {
  el.sideBuyBtn.onclick = async () => {
    store.currentSide = 'BUY';
    // keep selector coherent if empty
    try { resolveTokenIdForPlaceOrder(); } catch {}
    await placeFromForm('BUY');
    // After submitting, avoid WS teardown; fetch lists only
    scheduleOrdersRefresh();
    schedulePositionsRefresh();
  };
}

if (el.sideSellBtn) {
  el.sideSellBtn.onclick = async () => {
    store.currentSide = 'SELL';
    try { resolveTokenIdForPlaceOrder(); } catch {}
    await placeFromForm('SELL');
    scheduleOrdersRefresh();
    schedulePositionsRefresh();
  };
}

// Delegated click handler for one-click submit on orderbook levels
if (el.booksRow) {
  el.booksRow.addEventListener('click', async (ev: MouseEvent) => {
    try {
      const target = ev.target as HTMLElement | null;
      if (!target) return;
      
      // Quick Limit button
      if (target.dataset && target.dataset.role === 'limit-btn') {
        const limitCell = target.closest && target.closest('.limit-cell');
        if (!limitCell) return;
        const levelsEl = (target.closest && target.closest('.levels')) || limitCell.parentElement;
        if (!levelsEl) return;
        const assetId = (levelsEl as HTMLElement).dataset?.assetId;
        const sideStr = (levelsEl as HTMLElement).dataset?.side;
        if (!assetId || !sideStr) return;
        const priceCell = limitCell.previousElementSibling;
        const priceText = priceCell && priceCell.textContent ? priceCell.textContent : '';
        const price = Number(priceText);
        if (!(price > 0 && price <= 1)) return;
        const side = sideStr === 'ASK' ? 'SELL' : 'BUY';
        store.currentSide = side;
        try { if (assetSelect) assetSelect.value = assetId; } catch {}
        let sizeVal = 10;
        if (side === 'BUY') {
          sizeVal = el.quickLimitBuySize && Number(el.quickLimitBuySize.value) > 0 ? Math.floor(Number(el.quickLimitBuySize.value)) : 10;
          sizeVal = Math.max(5, sizeVal);
        } else {
          // SELL: use all owned shares for this asset
          try {
            const bal = store.positionsByAssetId.get(assetId);
            const owned = bal ? Math.floor(Number(bal) / 1e6) : 0;
            if (!(owned > 0)) { flash('', 'No owned shares to sell'); return; }
            sizeVal = owned;
          } catch { return; }
        }
        try {
          (target as HTMLButtonElement).disabled = true;
          if (orderResult) orderResult.textContent = '';
        const res = await safePlaceOrder({ tokenId: assetId, price, shares: sizeVal, side, orderType: 'GTC' });
          if (res && res.ok) {
            const usd = (res.data && 'notional' in res.data ? res.data.notional : undefined) ?? price * sizeVal;
            if (orderResult) orderResult.textContent = `Limit ${side} ${sizeVal} @ ${price.toFixed(2)} (USDC ${usd.toFixed(2)})`;
            scheduleOrdersRefresh();
            schedulePositionsRefresh();
            flash('ok', `Limit ${side} placed: ${sizeVal} @ ${price.toFixed(2)}`);
          } else {
            const errText = res && res.error ? res.error : 'unknown';
            if (orderResult) orderResult.textContent = 'Limit submit error: ' + errText;
            try {
              if (res && res.appError) notifications.showAppError(deserializeAppError(res.appError));
              else flash('err', 'Limit submit error: ' + errText);
            } catch {
              flash('err', 'Limit submit error: ' + errText);
            }
          }
        } catch (e) {
          const error = e as Error;
          if (orderResult) orderResult.textContent = 'Limit submit error: ' + (error?.message || e);
          flash('err', 'Limit submit error: ' + (error?.message || e));
        } finally {
          try { (target as HTMLButtonElement).disabled = false; } catch {}
        }
        return;
      }
      
      // Quick Buy/Sell explicit buttons
      if (target.dataset && target.dataset.role === 'quick-btn') {
        const actionsCell = target.closest && target.closest('.actions-cell');
        if (!actionsCell) return;
        const levelsEl = (target.closest && target.closest('.levels')) || actionsCell.parentElement;
        if (!levelsEl) return;
        const assetId = (levelsEl as HTMLElement).dataset?.assetId;
        const sideStr = (levelsEl as HTMLElement).dataset?.side;
        if (!assetId || !sideStr) return;
        const priceCell = actionsCell.nextElementSibling;
        const priceText = priceCell && priceCell.textContent ? priceCell.textContent : '';
        const price = Number(priceText);
        if (!(price > 0 && price <= 1)) return;
        let shares = Math.max(5, Number(quickSize?.value) > 0 ? Number(quickSize?.value) : 10);
        const actionSide = sideStr === 'ASK' ? 'BUY' : 'SELL';
        store.currentSide = actionSide;
        try { if (assetSelect) assetSelect.value = assetId; } catch {}
        if (orderResult) orderResult.textContent = '';
        const orderType = 'GTC';
        try {
          if (actionSide === 'SELL') {
            try {
              const bal = store.positionsByAssetId.get(assetId);
              const owned = bal ? Math.floor(Number(bal) / 1e6) : 0;
              if (!(owned > 0)) { flash('', 'No owned shares to sell'); return; }
              shares = owned;
            } catch { return; }
          }
          try { flash('', `Submitting quick ${actionSide} ${shares} @ ${price.toFixed(2)}…`); } catch {}
          const res = await safePlaceOrder({ tokenId: assetId, price, shares, side: actionSide, orderType });
          if (res && res.ok) {
            const usd = (res.data && 'notional' in res.data ? res.data.notional : undefined) ?? price * shares;
            if (orderResult) orderResult.textContent = `Quick ${actionSide} ${shares} @ ${price.toFixed(2)} (USDC ${usd.toFixed(2)})`;
            scheduleOrdersRefresh();
            schedulePositionsRefresh();
            flash('ok', `Quick ${actionSide} placed: ${shares} @ ${price.toFixed(2)}`);
          } else {
            const errText = res && res.error ? res.error : 'unknown';
            if (orderResult) orderResult.textContent = 'Submit error: ' + errText;
            try {
              if (res && res.appError) notifications.showAppError(deserializeAppError(res.appError));
              else flash('err', 'Submit error: ' + errText);
            } catch {
              flash('err', 'Submit error: ' + errText);
            }
          }
        } catch (e) {
          const error = e as Error;
          if (orderResult) orderResult.textContent = 'Submit error: ' + (error?.message || e);
          flash('err', 'Submit error: ' + (error?.message || e));
        }
        return;
      }
    } catch {}
  });
}

// Quick Limit Max Sell, wheel increments
if (el.quickLimitMaxSell) {
  el.quickLimitMaxSell.onclick = () => {
    try {
      const id = el.assetSelect && el.assetSelect.value;
      const bal = id ? store.positionsByAssetId.get(id) : null;
      const owned = bal ? Math.floor(Number(bal) / 1e6) : 0;
      if (owned > 0 && el.quickLimitSellSize) el.quickLimitSellSize.value = String(owned);
    } catch {}
  };
}

function bindWheelIncrement(inp: HTMLInputElement | null): void {
  if (!inp) return;
  inp.addEventListener('wheel', (e: WheelEvent) => {
    try { e.preventDefault(); } catch {}
    const cur = Math.floor(Number(inp.value) || 0);
    const step = 10;
    const dir = e.deltaY < 0 ? 1 : -1;
    let next = cur + dir * step;
    if (next < 0) next = 0;
    inp.value = String(next);
  }, { passive: false });
}

bindWheelIncrement(el.quickLimitBuySize);
bindWheelIncrement(el.quickLimitSellSize);

function bindEnterDismiss(inp: HTMLInputElement | null): void {
  if (!inp) return;
  try { inp.setAttribute('enterkeyhint', 'done'); } catch {}
  try { inp.setAttribute('autocomplete', 'off'); } catch {}
  inp.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      try { e.preventDefault(); } catch {}
      try { (e.target as HTMLInputElement)?.blur(); } catch {}
    }
  });
}

bindEnterDismiss(priceInp);
bindEnterDismiss(sizeInp);
bindEnterDismiss(quickSize);
bindEnterDismiss(el.quickLimitBuySize);
bindEnterDismiss(el.quickLimitSellSize);

if (el.quickLimitCancelAll) {
  el.quickLimitCancelAll.onclick = async () => {
    try {
      el.quickLimitCancelAll!.disabled = true;
      const res = await apiCancelAll();
      if (!res || !res.ok) {
        try {
          if (res && res.appError) notifications.showAppError(deserializeAppError(res.appError));
          else flash('err', 'Cancel all error');
        } catch {
          flash('err', 'Cancel all error');
        }
      }
      else scheduleOrdersRefresh();
    } finally {
      el.quickLimitCancelAll!.disabled = false;
    }
  };
}

// Rapid toggle removed

// Helper function to update orderbook status badge
function updateOrderbookStatus(): void {
  if (!obStatus) return;
  try {
    const now = Date.now();
    const dtHeartbeat = now - (store.obLastHeartbeatTs || 0);
    const dtRender = now - (store.obLastRenderTs || 0);
    const dtMessage = now - (store.obLastMessageTs || 0);
    const renderLag = (store.obLastMessageTs || 0) - (store.obLastRenderTs || 0);
    const pending = store.pendingEvents.length;
    
    // Build detailed tooltip
    const successRate = store.renderAttempts > 0 
      ? `${((store.renderSuccesses / store.renderAttempts) * 100).toFixed(1)}%`
      : 'N/A';
    const tooltip = [
      `State: ${store.obState}`,
      `Render: ${store.renderSuccesses}/${store.renderAttempts} success (${successRate})`,
      store.renderFailures > 0 ? `Failures: ${store.renderFailures}` : null,
      `Lag: ${renderLag}ms`,
      `Last render: ${Math.round(dtRender/1000)}s ago`,
      store.pendingEvents.length > 0 ? `Pending: ${store.pendingEvents.length} events` : null,
      store.obLastRenderError ? `Error: ${store.obLastRenderError}` : null,
    ].filter(Boolean).join(' | ');
    obStatus.title = tooltip;
    
    // Priority order: explicit states first, then derived from timestamps
    if (store.obState === 'render_error') {
      const shortError = store.obLastRenderError 
        ? (store.obLastRenderError.length > 30 
          ? store.obLastRenderError.slice(0, 30) + '…' 
          : store.obLastRenderError)
        : 'unknown';
      obStatus.textContent = `Orderbook: render error — ${shortError}`;
      obStatus.className = 'badge err';
    } else if (store.obState === 'closed') {
      obStatus.textContent = 'Orderbook: closed';
      obStatus.className = 'badge';
    } else if (store.obState === 'error') {
      obStatus.textContent = 'Orderbook: error';
      obStatus.className = 'badge err';
    } else if (store.obState === 'reconnecting') {
      obStatus.textContent = 'Orderbook: reconnecting…';
      obStatus.className = 'badge warn';
    } else if (dtHeartbeat > 30000) {
      // No heartbeat for 30s - connection issue
      store.obState = 'stale';
      obStatus.textContent = 'Orderbook: stale — press Refresh';
      obStatus.className = 'badge warn';
    } else if (renderLag > 5000 && dtMessage < 10000) {
      // Only show degraded if there is actual backlog or render is busy
      const hidden = (document && (document as any).hidden) ? true : false;
      const hasBacklog = (store.pendingEvents.length > 0) || store.activeRender;
      if (hasBacklog) {
        store.obState = 'degraded';
        obStatus.textContent = hidden
          ? `Orderbook: backgrounded (${Math.round(renderLag/1000)}s lag)`
          : `Orderbook: degraded (${Math.round(renderLag/1000)}s lag)`;
        obStatus.className = 'badge warn';
      } else {
        // No backlog: treat as live, this can happen if a single message arrived just before a render
        // But if we haven't seen a message for a bit, mark degraded on recency
        if (dtMessage > 1500) {
          store.obState = 'degraded';
          obStatus.textContent = `Orderbook: no recent updates (${Math.round(dtMessage/1000)}s)`;
          obStatus.className = 'badge warn';
        } else {
          store.obState = 'live';
          const backlog = store.pendingEvents.length > 100 ? ` ⏳${store.pendingEvents.length}` : '';
          const renderInfo = (store.renderFailures > 0 ? ` ⚠️${store.renderFailures}` : '') + backlog;
          obStatus.textContent = `Orderbook: live${renderInfo}`;
          obStatus.className = store.renderFailures > 0 ? 'badge warn' : 'badge ok';
        }
      }
    } else if (dtMessage > 3000) {
      // No messages for >10s — data feed likely stale
      store.obState = 'stale';
      obStatus.textContent = 'Orderbook: no updates — press Refresh';
      obStatus.className = 'badge warn';
    } else if (dtRender > 45000) {
      // Render hasn't changed for a while, but messages may still be coming
      obStatus.textContent = 'Orderbook: no visible changes';
      obStatus.className = 'badge';
    } else if (store.obState === 'live' || store.obState === 'degraded') {
      // All good - show failures only if >0
      const backlog = pending > 100 ? ` ⏳${pending}` : '';
      const renderInfo = (store.renderFailures > 0 ? ` ⚠️${store.renderFailures}` : '') + backlog;
      obStatus.textContent = `Orderbook: live${renderInfo}`;
      obStatus.className = store.renderFailures > 0 ? 'badge warn' : 'badge ok';
    } else if (store.obState === 'idle') {
      obStatus.textContent = 'Orderbook: idle';
      obStatus.className = 'badge';
    }
  } catch (err) {
    console.error('[Orderbook] Status update error:', err);
  }
}

// Periodic stale detector
setInterval(() => {
  updateOrderbookStatus();
}, 1000);

// Gentle auto-resubscribe if no book updates for 2 minutes
setInterval(async () => {
  try {
    const now = Date.now();
    const dtHeartbeat = now - (store.obLastHeartbeatTs || 0);
    const dtBook = now - (store.obLastBookTs || 0);
    if (dtHeartbeat <= 60000 && dtBook > 120000) {
      try {
        const slug = (el.slugInput && el.slugInput.value) || '';
        await sessionSubscribe(slug);
        if (obStatus) { obStatus.textContent = 'Orderbook: resubscribed'; obStatus.className = 'badge ok'; }
      } catch {}
    }
  } catch {}
}, 10000);

// Funds UI removed; keep placeholder to satisfy session controller API
async function loadFunds(): Promise<void> { /* no-op */ }

// Team mapping change handlers
if (teamAMap) {
  teamAMap.onchange = () => {
    try {
      if (_syncingTeamMap) return;
      _syncingTeamMap = true;
      // If A == B after change, move B to a different token id
      if (teamBMap && teamAMap && teamBMap.value === teamAMap.value) {
        const other = pickOtherTokenId(teamAMap.value);
        if (other) teamBMap.value = other;
      }
    } finally {
      _syncingTeamMap = false;
    }
    // Bump mapping version and record change time
    try { (store as any).teamMapVersion = ((store as any).teamMapVersion || 0) + 1; (store as any).lastTeamMapChangeTs = Date.now(); } catch {}
    // Keep asset dropdown in sync with active team selection, if any
    try {
      const aActive = !!(teamBtnA && teamBtnA.classList && teamBtnA.classList.contains('active'));
      const bActive = !!(teamBtnB && teamBtnB.classList && teamBtnB.classList.contains('active'));
      const desired = aActive ? (teamAMap && teamAMap.value) : bActive ? (teamBMap && teamBMap.value) : '';
      if (desired && assetSelect) assetSelect.value = desired;
    } catch {}
    syncTeamLabels();
    try { reorderBooks(); } catch {}
    // Persist mapping per current slug
    try {
      const slugKey = store.currentSlug || '';
      if (slugKey && teamAMap && teamBMap) {
        localStorage.setItem('pm_team_map_' + slugKey, JSON.stringify({ A: teamAMap.value, B: teamBMap.value }));
      }
    } catch {}
    // Update team buttons text on map change
    try {
      const idA = getTeamAssetId('A');
      const idB = getTeamAssetId('B');
      if (teamBtnA) teamBtnA.textContent = (store && store.books && store.books.get(idA) && store.books.get(idA)!.name) || teamBtnA.textContent;
      if (teamBtnB) teamBtnB.textContent = (store && store.books && store.books.get(idB) && store.books.get(idB)!.name) || teamBtnB.textContent;
    } catch {}
  };
}

if (teamBMap) {
  teamBMap.onchange = () => {
    try {
      if (_syncingTeamMap) return;
      _syncingTeamMap = true;
      // If B == A after change, move A to a different token id
      if (teamAMap && teamBMap && teamAMap.value === teamBMap.value) {
        const other = pickOtherTokenId(teamBMap.value);
        if (other) teamAMap.value = other;
      }
    } finally {
      _syncingTeamMap = false;
    }
    // Bump mapping version and record change time
    try { (store as any).teamMapVersion = ((store as any).teamMapVersion || 0) + 1; (store as any).lastTeamMapChangeTs = Date.now(); } catch {}
    // Keep asset dropdown in sync with active team selection, if any
    try {
      const aActive = !!(teamBtnA && teamBtnA.classList && teamBtnA.classList.contains('active'));
      const bActive = !!(teamBtnB && teamBtnB.classList && teamBtnB.classList.contains('active'));
      const desired = bActive ? (teamBMap && teamBMap.value) : aActive ? (teamAMap && teamAMap.value) : '';
      if (desired && assetSelect) assetSelect.value = desired;
    } catch {}
    syncTeamLabels();
    try { reorderBooks(); } catch {}
    try {
      const slugKey = store.currentSlug || '';
      if (slugKey && teamAMap && teamBMap) {
        localStorage.setItem('pm_team_map_' + slugKey, JSON.stringify({ A: teamAMap.value, B: teamBMap.value }));
      }
    } catch {}
    try {
      const idA = getTeamAssetId('A');
      const idB = getTeamAssetId('B');
      if (teamBtnA) teamBtnA.textContent = (store && store.books && store.books.get(idA) && store.books.get(idA)!.name) || teamBtnA.textContent;
      if (teamBtnB) teamBtnB.textContent = (store && store.books && store.books.get(idB) && store.books.get(idB)!.name) || teamBtnB.textContent;
    } catch {}
  };
}

// Initialize keyboard shortcuts binding
initShortcuts({
  books,
  updateNotional,
  placeOrder: apiPlaceOrder,
  setCurrentSide: (side: OrderSide) => { store.currentSide = side; },
  refreshOrderbook,
});

// Expose safe place order for shortcuts to use when available
try { (window as any).__pmSafePlaceOrder = safePlaceOrder; } catch {}

// Diagnostic tools - expose to window for debugging
if (typeof window !== 'undefined') {
  (window as any).orderbookDiagnostics = function() {
    const now = Date.now();
    const dtHeartbeat = now - (store.obLastHeartbeatTs || 0);
    const dtRender = now - (store.obLastRenderTs || 0);
    const dtMessage = now - (store.obLastMessageTs || 0);
    const dtBook = now - (store.obLastBookTs || 0);
    const renderLag = (store.obLastMessageTs || 0) - (store.obLastRenderTs || 0);
    
    console.log('=== Orderbook Diagnostics ===');
    console.log('Current State:', store.obState);
    console.log('Active Books:', store.books.size);
    console.log('\n--- Timing (ms ago) ---');
    console.log('Last Heartbeat:', dtHeartbeat, 'ms ago');
    console.log('Last Message:', dtMessage, 'ms ago');
    console.log('Last Book Update:', dtBook, 'ms ago');
    console.log('Last Successful Render:', dtRender, 'ms ago');
    console.log('Render Lag:', renderLag, 'ms');
    console.log('\n--- Render Metrics ---');
    console.log('Total Attempts:', store.renderAttempts);
    console.log('Successes:', store.renderSuccesses);
    console.log('Failures:', store.renderFailures);
    console.log('Success Rate:', store.renderAttempts > 0 ? `${((store.renderSuccesses / store.renderAttempts) * 100).toFixed(1)}%` : 'N/A');
    console.log('Active Render:', store.activeRender);
    console.log('Render Scheduled:', store.renderScheduled);
    console.log('Pending Events:', store.pendingEvents.length);
    if (store.obLastRenderError) {
      console.log('Last Render Error:', store.obLastRenderError);
    }
    console.log('\n--- Per-Book Status ---');
    store.books.forEach((book: Book, assetId: string) => {
      const shortId = assetId.slice(0, 10);
      const bidCount = book.bids.size;
      const askCount = book.asks.size;
      const isDirty = book.dirty ? 'DIRTY' : 'clean';
      const isConnected = (book.bidsLevels && book.bidsLevels.isConnected) ? 'connected' : 'DETACHED';
      console.log(`  ${shortId}: ${isDirty}, ${isConnected}, ${bidCount} bids, ${askCount} asks`);
    });
    console.log('\n--- Recommendations ---');
    if (dtHeartbeat > 30000) console.warn('⚠️ No heartbeat for >30s - connection may be dead');
    if (renderLag > 5000 && dtMessage < 10000) console.warn('⚠️ Render lag >5s - performance issue');
    if (store.renderFailures > 0) console.warn(`⚠️ ${store.renderFailures} render failures detected`);
    if (store.pendingEvents.length > 100) console.warn(`⚠️ ${store.pendingEvents.length} pending events - render backlog`);
    if (store.activeRender && dtMessage < 5000) console.warn('⚠️ Render active for >5s - possible hang');
    console.log('\nTip: Run window.orderbookDiagnostics() anytime to check status');
    console.log('===========================');
    
    return {
      state: store.obState,
      timing: { dtHeartbeat, dtMessage, dtBook, dtRender, renderLag },
      metrics: {
        attempts: store.renderAttempts,
        successes: store.renderSuccesses,
        failures: store.renderFailures,
        successRate: store.renderAttempts > 0 ? (store.renderSuccesses / store.renderAttempts) * 100 : 0,
      },
      activeBooks: store.books.size,
      pendingEvents: store.pendingEvents.length,
      lastError: store.obLastRenderError,
    };
  };
  console.log('💡 Orderbook diagnostics available: window.orderbookDiagnostics()');

  // Synthetic stress test to profile render lag under burst load
  (window as any).orderbookStressTest = async function(opts?: { assets?: number; ratePerSec?: number; durationMs?: number; }): Promise<any> {
    const assets = Math.max(1, Math.min(10, Number(opts?.assets ?? 2)));
    const rate = Math.max(10, Math.min(5000, Number(opts?.ratePerSec ?? 1000)));
    const durationMs = Math.max(500, Math.min(30000, Number(opts?.durationMs ?? 3000)));
    const assetIds: string[] = [];
    for (let i = 0; i < assets; i++) {
      const id = `stress_${i.toString().padStart(2, '0')}`;
      assetIds.push(id);
      try { ensurePanel(id, `Stress ${i+1}`); } catch {}
    }
    const genChange = (id: string) => {
      const side = Math.random() < 0.5 ? 'BUY' : 'SELL';
      const price = Math.max(0.01, Math.min(0.99, Math.round((Math.random()*0.98 + 0.01) * 100) / 100));
      const size = Math.floor(Math.random() * 500) + 1;
      return { event_type: 'price_change', asset_id: id, changes: [{ side, price: String(price), size: String(size) }] };
    };
    const periodMs = 1000 / rate; // average period between events
    const start = Date.now();
    const end = start + durationMs;
    const samples: number[] = [];
    let maxPending = 0;
    let rafRunning = true;
    const rafLoop = () => {
      const lag = (store.obLastMessageTs || 0) - (store.obLastRenderTs || 0);
      samples.push(Math.max(0, lag));
      try { maxPending = Math.max(maxPending, store.pendingEvents.length || 0); } catch {}
      if (rafRunning) requestAnimationFrame(rafLoop);
    };
    requestAnimationFrame(rafLoop);
    let nextAt = Date.now();
    while (Date.now() < end) {
      const id = assetIds[Math.floor(Math.random() * assetIds.length)]!;
      const evt = genChange(id);
      try { orderbook.applyText(JSON.stringify(evt)); } catch {}
      nextAt += periodMs;
      const sleepMs = Math.max(0, Math.min(50, Math.floor(nextAt - Date.now())));
      if (sleepMs > 0) await new Promise((r) => setTimeout(r, sleepMs));
    }
    rafRunning = false;
    // Compute percentiles
    const sorted = samples.slice().sort((a,b)=>a-b);
    const pct = (p: number) => sorted[Math.min(sorted.length-1, Math.max(0, Math.floor((p/100)*sorted.length)))] || 0;
    const result = {
      samples: samples.length,
      p50: pct(50), p95: pct(95), p99: pct(99),
      maxPending,
      attempts: store.renderAttempts,
      successes: store.renderSuccesses,
      failures: store.renderFailures,
    };
    console.log('=== Orderbook Stress Test ===');
    console.log('Assets:', assets, 'Rate/s:', rate, 'Duration(ms):', durationMs);
    console.log('Samples:', result.samples, 'p50:', result.p50, 'ms', 'p95:', result.p95, 'ms', 'p99:', result.p99, 'ms');
    console.log('Max pending events:', result.maxPending, 'Attempts:', result.attempts, 'Successes:', result.successes, 'Failures:', result.failures);
    return result;
  };
  console.log('💡 Stress test available: window.orderbookStressTest({ assets, ratePerSec, durationMs })');
}

function timeAgo(ts: string | number | undefined): string {
  try {
    // ts can be ISO string or number (seconds), normalize
    let t: number;
    if (typeof ts === 'string') t = new Date(ts).getTime();
    else if (typeof ts === 'number') t = ts > 1e12 ? ts : ts * 1000;
    else t = 0;
    const delta = Math.max(0, Date.now() - (isFinite(t) ? t : 0));
    const m = Math.floor(delta / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return m + 'm ago';
    const h = Math.floor(m / 60);
    if (h < 24) return h + 'h ago';
    const d = Math.floor(h / 24);
    return d + 'd ago';
  } catch { return ''; }
}

function formatCents(n: number | string): string {
  const v = Number(n);
  if (!isFinite(v)) return '-';
  return (v * 100).toFixed(1) + '¢';
}

function formatUsd(price: number | string, shares: number | string): string {
  const p = Number(price);
  const s = Number(shares);
  if (!(isFinite(p) && isFinite(s))) return '$-';
  return `$${(p * s).toFixed(0)}`;
}

function shortAddress(addr: string | undefined): string {
  if (!addr || typeof addr !== 'string') return '';
  return addr.slice(0, 6) + '…' + addr.slice(-4);
}

function outcomeName(assetId: string | undefined): string {
  try {
    const book = assetId ? books.get(assetId) : null;
    return (book && book.name) || (assetId ? assetId.slice(0, 8) + '…' : '');
  } catch { return ''; }
}

async function loadActivity(): Promise<void> {
  try {
    if (!activityEl) return;
    const res = await getMarketActivity({ limit: 50 });
    if (!res || !res.ok) {
      try {
        if (res && res.appError) notifications.showAppError(deserializeAppError(res.appError));
      } catch {}
      activityEl.textContent = 'Error loading activity';
      return;
    }
    const list = Array.isArray(res.data) ? res.data : [] as unknown[];
    activityEl.innerHTML = '';
    for (const t of list as any[]) {
      try {
        const size = Math.floor(Number(t.size) || 0);
        const price = Number(t.price);
        const sideWord = String(t.side || '').toUpperCase() === 'SELL' ? 'sold' : 'bought';
        const rawUser = t.username && String(t.username).trim() ? String(t.username) : (t.address ? shortAddress(t.address) : 'user');
        const username = rawUser.length > 21 ? rawUser.slice(0, 21) : rawUser;
        const team = t.outcome || outcomeName(t.asset_id);
        const cents = formatCents(price);
        const usd = formatUsd(price, size);
        const ago = timeAgo(t.timestamp || t.match_time || t.last_update);

        const row = document.createElement('div');

        const userSpan = document.createElement('span');
        userSpan.textContent = username;
        const actionSpan = document.createElement('span');
        actionSpan.textContent = sideWord;
        actionSpan.className = sideWord === 'bought' ? 'buy-word' : 'sell-word';
        const sizeSpan = document.createElement('span');
        sizeSpan.textContent = String(size);
        const teamSpan = document.createElement('span');
        teamSpan.textContent = team;

        // Apply team color to team name based on mapped panel
        try {
          const assetId = t && (t.asset_id || t.asset);
          if (assetId) {
            const panel = document.querySelector(`.panel[data-asset-id="${assetId}"]`);
            if (panel && panel.classList && panel.classList.contains('team-a')) teamSpan.className = 'team-a-name';
            else if (panel && panel.classList && panel.classList.contains('team-b')) teamSpan.className = 'team-b-name';
          }
        } catch {}

        row.appendChild(userSpan);
        row.appendChild(document.createTextNode(' '));
        row.appendChild(actionSpan);
        row.appendChild(document.createTextNode(' '));
        row.appendChild(sizeSpan);
        row.appendChild(document.createTextNode(' '));
        row.appendChild(teamSpan);
        row.appendChild(document.createTextNode(' at ' + cents + ' (' + usd + ') ' + ago));

        activityEl.appendChild(row);
      } catch {}
    }
  } catch (e) {
    const error = e as Error;
    if (activityEl) activityEl.textContent = 'Error: ' + (error?.message || e);
  }
}


